import { useEffect, useState } from 'react';
import { IonSpinner } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { RadioOptionList } from '../ui/RadioOptionList';

// `labelEn` is what we ship to the BE in `cancellationReason` — keeping it in
// English preserves analytics/log readability regardless of which lang the
// lister picked, matching the per-order RejectOrderModal convention. The
// displayed text comes from publish.json via t() at render time, and `key`
// matches the publish.json sub-key so the lookup is direct.
const LISTING_CANCEL_REASONS = [
  { key: 'no_longer_cooking', labelEn: "I'm no longer cooking this" },
  { key: 'schedule', labelEn: 'My schedule changed' },
  { key: 'ingredients', labelEn: 'Ingredient or supply problem' },
  { key: 'not_enough_orders', labelEn: 'Not enough orders' },
  { key: 'wrong_details', labelEn: 'Listing details were wrong' },
  { key: 'other', labelEn: 'Other' },
] as const;

interface CancelListingModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Receives the reason string when `requireReason` is set; '' otherwise. */
  onConfirm: (reason: string) => void;
  isPending?: boolean;
  error?: string | null;
  /**
   * Show the reason picker and block confirm until one is chosen. Set on the
   * cancel-the-whole-listing paths, whose backend patch rejects a status flip
   * to 'canceled' without a `cancellationReason`. Left off for the variants
   * that aren't cancellations (draft delete, "stop taking orders").
   */
  requireReason?: boolean;
  /** Override the default title. Pass `null` (or leave undefined) to use the
   *  publish-namespace default copy. */
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

export const CancelListingModal: React.FC<CancelListingModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isPending = false,
  error = null,
  requireReason = false,
  title,
  description,
  confirmLabel,
  cancelLabel,
}) => {
  const { t } = useTranslation('publish');
  // Defaults resolve via i18next so a Spanish reader sees the Spanish
  // default copy; callers override with already-translated strings (the
  // cancel-and-copy variant builds its own copy in PublishListingPage).
  const resolvedTitle = title ?? t('cancelModal.titleDefault');
  const resolvedBody = description ?? t('cancelModal.bodyDefault');
  const resolvedConfirm = confirmLabel ?? t('cancelModal.confirmDefault');
  const resolvedCancel = cancelLabel ?? t('cancelModal.cancelDefault');

  const [selected, setSelected] = useState<string | null>(null);
  const [otherText, setOtherText] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelected(null);
      setOtherText('');
    }
  }, [isOpen]);

  // Resolved reason = wire payload. For canned options we send the EN label so
  // BE analytics stay stable; for "other" we send the lister's free-text in
  // whatever language they typed, falling back to the EN "Other" label so the
  // textarea stays optional.
  const resolvedReason = (() => {
    if (!requireReason || !selected) return '';
    const option = LISTING_CANCEL_REASONS.find((r) => r.key === selected);
    if (selected === 'other') return otherText.trim() || option?.labelEn || 'Other';
    return option?.labelEn ?? '';
  })();

  const canConfirm = !isPending && (!requireReason || !!selected);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <h3 className="text-lg font-bold mb-4 pr-8">{resolvedTitle}</h3>

      <p className={`text-sm text-muted-foreground ${requireReason ? 'mb-4' : 'mb-6'}`}>
        {resolvedBody}
      </p>

      {requireReason && (
        <>
          <p className="text-sm font-medium text-foreground mb-2">
            {t('cancelModal.reasonPrompt')}
          </p>
          <RadioOptionList
            className="mb-4"
            ariaLabel={t('cancelModal.reasonPrompt')}
            options={LISTING_CANCEL_REASONS.map((reason) => ({
              key: reason.key,
              label: t(`cancelModal.reasons.${reason.key}`),
            }))}
            value={selected}
            onChange={setSelected}
            disabled={isPending}
          />

          {selected === 'other' && (
            <textarea
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              placeholder={t('cancelModal.otherPlaceholder')}
              rows={3}
              className="w-full rounded-lg border border-border bg-background text-foreground px-3 py-2 text-sm focus:border-brand-primary focus:outline-none resize-none mb-4"
            />
          )}
        </>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={isPending}
          className="flex-1 rounded-lg btn-outline border-border py-2.5 text-sm font-semibold text-foreground active:opacity-80 disabled:opacity-50"
        >
          {resolvedCancel}
        </button>
        <button
          type="button"
          onClick={() => onConfirm(resolvedReason)}
          disabled={!canConfirm}
          className="flex-1 rounded-lg bg-brand-danger py-2.5 text-sm font-semibold text-white active:opacity-80 disabled:opacity-50 flex items-center justify-center"
        >
          {isPending ? (
            <IonSpinner name="crescent" className="h-4 w-4" />
          ) : (
            resolvedConfirm
          )}
        </button>
      </div>
    </Modal>
  );
};
